import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setSelectOpen } from '../../features/Select/selectOpenSlice';

interface Option {
    value: string;
    label: string;
}

interface SelectProps {
    options: Option[];
    value?: string | string[];
    onChange: (value: string | string[]) => void;
    placeholder?: string;
    multiple?: boolean;
    className?: string;
    allowClear?: boolean;
}

const Select: React.FC<SelectProps> = ({
    options,
    value,
    onChange,
    placeholder,
    multiple = false,
    className = '',
    allowClear = false,
}) => {
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState<string | string[]>('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
                dispatch(setSelectOpen(false));
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (value) {
            setSelectedValue(value);
        }
    }, [value]);

    const handleSelect = (option: Option) => {
        if (multiple) {
            const newValue = (selectedValue as string[]).includes(option.value)
                ? (selectedValue as string[]).filter(
                      (val) => val !== option.value
                  )
                : [...(selectedValue as string[]), option.value];
            setSelectedValue(newValue);
            onChange(newValue);
        } else {
            setSelectedValue(option.value);
            onChange(option.value);
            setIsOpen(false);
            dispatch(setSelectOpen(false));
        }
    };

    const displayValue = () => {
        if (
            !selectedValue ||
            (Array.isArray(selectedValue) && selectedValue.length === 0)
        ) {
            return placeholder || 'Select...';
        }

        if (Array.isArray(selectedValue)) {
            return selectedValue
                .map(
                    (val) =>
                        options.find((option) => option.value === val)?.label
                )
                .join(', ');
        }

        return options.find((option) => option.value === selectedValue)?.label;
    };

    const handleClear = () => {
        setSelectedValue(multiple ? [] : '');
        onChange(multiple ? [] : '');
    };

    return (
        <div
            className={`relative min-w-[4rem] ${className} `}
            ref={dropdownRef}
        >
            <div
                onClick={() => {
                    setIsOpen(!isOpen);
                    dispatch(setSelectOpen(!isOpen));
                }}
                className="bg-white border border-gray-300 rounded px-4 py-2 flex justify-between items-center cursor-pointer z-50"
            >
                <span
                    className={selectedValue ? 'text-black' : 'text-gray-400'}
                >
                    {displayValue()}
                </span>
                <div className="flex items-center">
                    {allowClear && selectedValue && (
                        <button
                            data-testid="select-clear-button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClear();
                            }}
                            className="text-gray-500 hover:text-gray-700 text-[0.5rem] p-1 bg-slate-200 rounded"
                        >
                            &#x2715;
                        </button>
                    )}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-5 w-5 transition-transform ${
                            isOpen ? 'transform rotate-180' : ''
                        }`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fillRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clipRule="evenodd"
                        />
                    </svg>
                </div>
            </div>
            {isOpen && (
                <div className="absolute z-50 mt-2 bg-white border border-gray-300 rounded shadow-lg max-h-60 w-full overflow-y-scroll custom-scrollbar">
                    {options.map((option) => (
                        <div
                            key={option.value}
                            onClick={() => handleSelect(option)}
                            className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${
                                multiple
                                    ? (selectedValue as string[]).includes(
                                          option.value
                                      )
                                        ? 'bg-blue-200 bg-opacity-50 text-blue-700'
                                        : ''
                                    : selectedValue === option.value
                                      ? 'bg-blue-200 bg-opacity-50 text-blue-700'
                                      : ''
                            }`}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default React.memo(Select);
